import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import ArrowBackIcon from '@material-ui/icons/ArrowBackOutlined';
import EditIcon from '@material-ui/icons/EditOutlined';
import { Button, makeStyles } from '@material-ui/core';
import ReceptionForm from 'components/Forms/ReceptionForm';
import RequiredFieldsLegend from 'components/Forms/RequiredFieldsLegend';
import BottomButtonBar from 'components/Generic/BottomButtonBar';
import PageContent from 'components/Generic/PageContent';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import { Routes } from 'routes';
import { postReception } from 'services/apiOperations';
import { DialogWidth, ReceptionForm as ReceptionFormType, SubmissionError } from 'types';
import { formatReceptionBeforePost } from 'utils/normalizer';
import Dialog from 'components/Dialog';
import { colors } from 'theme';
import { goBack } from 'utils/history';
import { DUPLICATE_RECEPTION_VIOLATION } from 'services/errors';

const useStyles = makeStyles({
  buttonBar: {
    display: 'flex',
    justifyContent: 'center',
    '& > *': {
      margin: '0 10px',
    },
  },
});

const ReceptionCreate: React.FC = () => {
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const classes = useStyles();

  const history = useHistory();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDuplicateModalOpen, setDuplicateModalOpen] = useState<boolean>(false);

  const onSubmit = async (reception: ReceptionFormType) => {
    if (isSubmitting || !sortingCenter) {
      return;
    }

    const formattedReception = {
      ...formatReceptionBeforePost(reception),
      sortingCenter: sortingCenter['@id'],
    };

    setIsSubmitting(true);
    postReception(formattedReception)
      .then(() => {
        notify({
          message: t('receptionForm.creation.success'),
          severity: 'success',
        });
        goBack(Routes.Receptions, history);
        setIsSubmitting(false);
      })
      .catch((e: SubmissionError) => {
        setIsSubmitting(false);
        if (e.status === 412) {
          if (e.response.body.violations) {
            const duplicateViolation = e.response.body.violations.find(
              violation => violation.message === DUPLICATE_RECEPTION_VIOLATION,
            );
            if (duplicateViolation) {
              setDuplicateModalOpen(true);
              return;
            }
          }
        }
        notify({
          message: t('errorGeneric'),
          severity: 'error',
        });
      });
  };

  return (
    <>
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={isDuplicateModalOpen}
        title={t('receptions.create.duplicateModal.title')}
        width={DialogWidth.DEFAULT}
        renderContent={() => (
          <div className={classes.buttonBar}>
            <Button
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              onClick={() => {
                goBack(Routes.Receptions, history);
              }}
            >
              {t('receptions.create.duplicateModal.backToList')}
            </Button>
            <Button
              endIcon={<EditIcon />}
              variant="outlined"
              onClick={() => {
                setDuplicateModalOpen(false);
              }}
              style={{
                background: colors.grey900,
                color: 'white',
              }}
            >
              {t('receptions.create.duplicateModal.edit')}
            </Button>
          </div>
        )}
      />
      <PageContent title={t('receptions.create.title')} backRoute="/receptions" withButtonBar>
        <ReceptionForm onSubmit={onSubmit} />
        <RequiredFieldsLegend />
      </PageContent>
      <BottomButtonBar
        formName="reception_form"
        previousStepLabel={t('receptions.create.cancelButtonLabel')}
        onClickPrevious={() => goBack(Routes.Receptions, history)}
        disableNext={isSubmitting}
        nextStepLabel={t('receptions.create.createButtonLabel')}
      />
    </>
  );
};
export default ReceptionCreate;
